import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import * as Sentry from '@sentry/react';
import { map } from 'rxjs';
import { createCustomFetch } from '@lib/utils/customFetch';
import isAbortError from '@lib/utils/isAbortError';
import { createLoggingLink } from '@lib/services/logger';
import {
  hasTechnicalPauseError,
  handleTechnicalPauseError,
} from '@lib/utils/technicalPauseErrorHandler';
import { handleGraphQLErrors } from '@lib/utils/versionMismatchErrorHandler';
import { captureException } from '@lib/utils/captureException';

const { VITE_AUTH_SCOPE, MODE } = import.meta.env;

if (MODE && ['development', 'test'].includes(MODE)) {
  loadDevMessages();
  loadErrorMessages();
}

const customHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  'X-Auth-Scope': VITE_AUTH_SCOPE,
  'Cache-Control': 'no-cache',
};

const cors = {
  xhrFields: { withCredentials: true },
  crossDomain: true,
  mode: 'cors',
  credentials: 'include',
} as const;

const singleHttpLink = new HttpLink({
  uri: `${import.meta.env.VITE_API_URL}/graphql`,
  headers: customHeaders,
  fetch: createCustomFetch,
  ...cors,
});

const errorLink = new ErrorLink(({ error }) => {
  const graphQLErrors = CombinedGraphQLErrors.is(error)
    ? error.errors
    : undefined;
  const networkError = CombinedGraphQLErrors.is(error) ? undefined : error;

  if (hasTechnicalPauseError(graphQLErrors)) {
    handleTechnicalPauseError(graphQLErrors);
  }

  handleGraphQLErrors(graphQLErrors, captureException);

  const statusCode = ServerError.is(networkError)
    ? networkError.statusCode
    : undefined;
  // AbortError here means the request was cancelled deliberately (component
  // unmount, a newer query superseding this one, page navigation) - not a
  // real failure.
  if (networkError && statusCode !== 429 && !isAbortError(networkError)) {
    captureException(networkError);
  }
});

let hasFetchedToken = false;
const optionsRequestLink = new ApolloLink((operation, forward) => {
  if (hasFetchedToken) {
    // If token has been fetched, proceed with the operation
    return forward(operation);
  }
  return new Observable((observer) => {
    // Send an OPTIONS request
    fetch('/graphql', {
      method: 'OPTIONS',
      credentials: 'include',
    })
      .then(() => {
        hasFetchedToken = true;
      })
      .finally(() => {
        // Proceed with the original request
        forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch((error) => {
        observer.error(error);
      });
  });
});

const sentryTracingLink = new ApolloLink((operation, forward) => {
  return Sentry.startSpan(
    { name: operation.operationName || 'GraphQL Request', op: 'graphql' },
    (span) => {
      return forward(operation).pipe(
        map((response) => {
          span?.end();
          return response;
        }),
      );
    },
  );
});

const loggingLink = createLoggingLink();

const link = ApolloLink.from([
  loggingLink,
  sentryTracingLink,
  optionsRequestLink,
  errorLink,
  singleHttpLink,
]);

const Client = new ApolloClient({
  cache: new InMemoryCache(),
  link,
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
  } as unknown as ApolloClient.DefaultOptions.Input,
});

export default Client;
